import type { Charges } from '@/v2/services/availability/types/api/parsedResponse';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Container,
  Flex,
  LoadingIndicator,
  Text,
} from '@qga/roo-ui/components';

import getCheapestFlightOption from '@shared/utils/getCheapestFlightOption/getCheapestFlightOption';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';
import {
  FlightJourney,
  Flights,
  SplitFlightOptions,
} from '@shared/v2/services/types/api/parsedResponse';

import FlightsTerms from '@/components/FlightsTerms';
import FullPageModal from '@/components/FullPageModal';
import { longDate } from '@/lib/date/format';
import { useFlightModal } from '@/v2/components/pages/PackageView/contexts/FlightModalContext';
import useFlightOptionInteractionsEvent from '@/v2/hooks/useFlightOptionInteractionsEvent';
import useFlightViewItemListEvent from '@/v2/hooks/useFlightViewItemListEvent';
import FlightSummary from '../components/FlightSummary';
import {
  FLIGHT_DIRECTION,
  FlightDirection,
} from '../constants/flightDirection';
import { flightConfirmedDataLayerEvent } from '../utils/dataLayer';
import getOfferCharges from '../utils/getOfferCharges';
import DirectionTabs from './components/DirectionTabs';
import FlightList from './components/FlightList';

interface StandardFlightOptionsProps {
  loading?: boolean;
  flightOptions?: SplitFlightOptions;
  charges: Charges | undefined;
  onConfirmFlights: (segments: { segments: Flights }) => void;
  offerId?: string;
}

export const StandardFlightOptions = React.memo(
  ({
    loading,
    flightOptions,
    charges,
    onConfirmFlights,
    offerId,
  }: StandardFlightOptionsProps) => {
    const { searchQuery } = useSearchQuery();

    const flightListRef = useRef(null);

    const [selectedOutboundOption, setOutboundOption] =
      useState<FlightJourney>();
    const [selectedInboundOption, setInboundOption] = useState<FlightJourney>();
    const [direction, setDirection] = useState<FlightDirection>(
      FLIGHT_DIRECTION.Departing,
    );
    const { closeFlightModal } = useFlightModal();
    useFlightViewItemListEvent({
      flightOptions:
        direction === FLIGHT_DIRECTION.Departing
          ? flightOptions?.outbound
          : flightOptions?.inbound,
      flightDirection: direction,
    });
    const { handleInvokeFlightOptionInteractionsEvent } =
      useFlightOptionInteractionsEvent({
        availablePackageCount:
          direction === FLIGHT_DIRECTION.Departing
            ? flightOptions?.outbound.length
            : flightOptions?.inbound.length,
        flightDirection: direction,
      });

    useEffect(() => {
      if (flightOptions && !selectedOutboundOption && !selectedInboundOption) {
        const { cheapestOutbound, cheapestInbound } =
          getCheapestFlightOption(flightOptions);

        const selectedOption = {
          outbound: cheapestOutbound,
          inbound: cheapestInbound,
        };

        setOutboundOption(selectedOption.outbound);
        setInboundOption(selectedOption.inbound);
      }
    }, [
      flightOptions,
      selectedOutboundOption,
      selectedInboundOption,
      searchQuery,
    ]);

    const scrollToFlightList = useCallback(() => {
      const flightListEl = flightListRef?.current as HTMLDivElement | null;
      if (flightListEl) {
        if (typeof flightListEl?.scroll !== 'function') return;

        flightListEl.scroll({
          top: 0,
        });
      }
    }, [flightListRef]);

    const handleClose = () => {
      closeFlightModal();
    };

    const setActiveTabWithScroll = (direction: FlightDirection) => {
      scrollToFlightList();
      setDirection(direction);
    };

    const displayRouteText = (
      tab: FlightDirection,
      options?: SplitFlightOptions,
    ) => {
      if (!options) return null;
      const { flightSegments } = options.outbound[0];
      const { origin } = flightSegments[0];
      const { destination } = flightSegments[flightSegments.length - 1];

      return tab === FLIGHT_DIRECTION.Departing
        ? `${origin.name} (${origin.code}) to ${destination.name} (${destination.code})`
        : `${destination.name} (${destination.code}) to ${origin.name} (${origin.code})`;
    };

    const onDone = () => {
      const flightOption =
        direction === FLIGHT_DIRECTION.Departing
          ? selectedOutboundOption
          : selectedInboundOption;

      if (flightOption && offerCharges) {
        handleInvokeFlightOptionInteractionsEvent({
          flightOption: flightOption,
          packagePrice: Number(offerCharges.total.amount),
          action: 'add',
        });
      }

      const directionSegment: {
        [key in FlightDirection]: keyof Flights;
      } = {
        [FLIGHT_DIRECTION.Departing]: 'outbound',
        [FLIGHT_DIRECTION.Returning]: 'inbound',
      };

      const selectedOption = {
        [FLIGHT_DIRECTION.Departing]: selectedOutboundOption,
        [FLIGHT_DIRECTION.Returning]: selectedInboundOption,
      }[direction];

      const index =
        flightOptions?.[directionSegment[direction]]?.findIndex(
          (option) => option === selectedOption,
        ) ?? -1;

      // Fire analytics event
      if (index >= 0) {
        flightConfirmedDataLayerEvent({
          flightDirection: direction,
          flightIndex: index + 1,
        });
      }

      if (direction === FLIGHT_DIRECTION.Departing) {
        setDirection(FLIGHT_DIRECTION.Returning);
      } else {
        const segments = {
          outbound: selectedOutboundOption?.flightSegments || [],
          inbound: selectedInboundOption?.flightSegments || [],
        };

        onConfirmFlights({ segments });
      }
    };

    const offerCharges = useMemo(() => {
      return (
        charges &&
        selectedInboundOption &&
        selectedOutboundOption &&
        getOfferCharges({
          offer: {
            charges: charges,
          },
          selectedInboundOption,
          selectedOutboundOption,
        })
      );
    }, [charges, selectedInboundOption, selectedOutboundOption]);

    return (
      <FullPageModal onClose={handleClose}>
        <FullPageModal.Header>
          <Text fontWeight="bold" fontSize="md">
            Flights selection
          </Text>
        </FullPageModal.Header>
        {/* @ts-ignore */}
        <FullPageModal.Body
          bg="greys.porcelain"
          data-testid="FLIGHT_OPTIONS"
          ref={flightListRef}
        >
          <Box
            position="sticky"
            top="0"
            left="0"
            right="0"
            bg="white"
            borderBottom="1px solid"
            borderColor="greys.alto"
            // @ts-expect-error "popup" is a valid custom z-index
            zIndex="popup"
            textAlign="center"
          >
            <Box>
              <DirectionTabs
                activeTab={direction}
                onTabClicked={setActiveTabWithScroll}
              />
            </Box>
            <Box pt="4">
              <Text fontWeight="bold" fontSize="sm">
                {displayRouteText(direction, flightOptions)}
              </Text>
            </Box>
            <Box pb="4">
              <Text fontSize="sm" color="greys.steel">
                {direction === FLIGHT_DIRECTION.Departing
                  ? longDate(searchQuery?.departureDate)
                  : longDate(searchQuery?.returnDate)}
              </Text>
            </Box>
          </Box>
          {loading ? (
            <Flex
              mt="10"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Box mb="4">We’re loading your flight options</Box>
              {/* @ts-expect-error color prop is optional */}
              <LoadingIndicator />
            </Flex>
          ) : (
            <>
              <Box py={['6', null, '6']} px={1}>
                <Container>
                  {offerCharges && (
                    <FlightList
                      selectedFlightOption={{
                        outbound: selectedOutboundOption,
                        inbound: selectedInboundOption,
                      }}
                      setSelectedFlightOption={{
                        setOutbound: setOutboundOption,
                        setInbound: setInboundOption,
                      }}
                      flightOptions={flightOptions}
                      flightDirection={direction}
                      charges={offerCharges}
                      offerId={offerId}
                    />
                  )}
                  <Box mt="4">
                    <FlightsTerms />
                  </Box>
                </Container>
              </Box>
              {offerCharges && (
                <FlightSummary
                  onDone={onDone}
                  charges={offerCharges}
                  direction={direction}
                  offerId={offerId}
                />
              )}
            </>
          )}
        </FullPageModal.Body>
      </FullPageModal>
    );
  },
);
